import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, User, Eye, Shield } from 'lucide-react';
import { CyberGrid } from '../components/CyberGrid';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  author?: string;
  tags: string[];
  featuredImage?: string;
  views: number;
  createdAt: string;
}

function getCachedPost(slug: string): BlogPost | null {
  try {
    const raw = sessionStorage.getItem(`blog_post_${slug}`);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > 10 * 60 * 1000) return null; // 10 min TTL
    return data;
  } catch {
    return null;
  }
}

function cachePost(slug: string, post: BlogPost) {
  try {
    sessionStorage.setItem(`blog_post_${slug}`, JSON.stringify({ data: post, timestamp: Date.now() }));
  } catch {}
}

const SkeletonHero = () => (
  <section className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white py-12 md:py-16 overflow-hidden">
    <CyberGrid tone="dark" animate opacity={0.08} wash={false} className="z-0" />
    <div aria-hidden className="absolute inset-0 pointer-events-none z-0">
      <div className="absolute -top-24 left-10 w-[520px] h-[520px] rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="absolute -bottom-24 right-10 w-[520px] h-[520px] rounded-full bg-indigo-400/10 blur-3xl" />
    </div>
    <div className="max-w-5xl mx-auto px-4 relative z-10 animate-pulse">
      <div className="h-6 w-28 bg-white/20 rounded-full mb-6" />
      <div className="h-8 w-40 bg-white/10 rounded-full mb-6" />
      <div className="h-10 w-full bg-white/20 rounded mb-3" />
      <div className="h-10 w-3/4 bg-white/20 rounded mb-5" />
      <div className="flex gap-4">
        <div className="h-5 w-24 bg-white/15 rounded-full" />
        <div className="h-5 w-20 bg-white/15 rounded-full" />
        <div className="h-5 w-18 bg-white/15 rounded-full" />
      </div>
    </div>
  </section>
);

const SkeletonBody = () => (
  <section className="relative bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100/60 overflow-hidden">
    <CyberGrid opacity={0.05} wash className="z-0" />
    <main className="relative z-10 max-w-5xl mx-auto px-4 py-10 animate-pulse">
      <div className="bg-white/90 border border-slate-200 rounded-2xl shadow-lg p-8 md:p-10">
        <div className="space-y-3">
          {[...Array(8)].map((_, i) => (
            <div key={i} className={`h-4 bg-slate-200 rounded ${i % 4 === 3 ? 'w-2/3' : 'w-full'}`} />
          ))}
          <div className="py-2" />
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`h-4 bg-slate-200 rounded ${i % 3 === 2 ? 'w-1/2' : 'w-full'}`} />
          ))}
        </div>
      </div>
    </main>
  </section>
);

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const fetchedOnceRef = useRef(false);

  useEffect(() => {
    if (!slug) return;

    // Show cached post immediately while fetching fresh data
    const cached = getCachedPost(slug);
    if (cached) {
      setPost(cached);
      setLoading(false);
    }

    if (fetchedOnceRef.current) return;
    fetchedOnceRef.current = true;

    axios.get(`${API_BASE_URL}/api/blog/posts/${slug}`, { timeout: 30000 })
      .then((res) => {
        setPost(res.data);
        setNotFound(false);
        cachePost(slug, res.data);
      })
      .catch(() => {
        if (!cached) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading && !post) {
    return (
      <div className="min-h-screen">
        <SkeletonHero />
        <SkeletonBody />
      </div>
    );
  }

  if (notFound && !post) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100/60">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-slate-700 font-semibold hover:text-slate-900"
          >
            <ArrowLeft size={18} /> Back to Blog
          </Link>
          <h1 className="text-3xl font-extrabold text-slate-900 mt-6">Post not found</h1>
          <p className="text-slate-600 mt-2">
            The article you're looking for may have been moved or unpublished.
          </p>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen">
      {/* HERO — enterprise slate */}
      <section className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white py-12 md:py-16 overflow-hidden">
        <CyberGrid tone="dark" animate opacity={0.08} wash={false} className="z-0" />

        {/* Soft spotlights (static) */}
        <div aria-hidden className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute -top-24 left-10 w-[520px] h-[520px] rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="absolute -bottom-24 right-10 w-[520px] h-[520px] rounded-full bg-indigo-400/10 blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-slate-200 font-semibold hover:text-white"
          >
            <ArrowLeft size={18} /> Back to Blog
          </Link>

          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 text-slate-200 text-sm font-semibold backdrop-blur-sm">
            <Shield size={16} className="text-cyan-300" />
            <span>CYVANTIX INSIGHTS</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-tight mt-4">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-slate-300 mt-5">
            <span className="inline-flex items-center gap-2 text-sm">
              <Calendar size={16} className="text-slate-300" />
              {new Date(post.createdAt).toLocaleDateString()}
            </span>

            {post.author && (
              <span className="inline-flex items-center gap-2 text-sm">
                <User size={16} className="text-slate-300" />
                {post.author}
              </span>
            )}

            <span className="inline-flex items-center gap-2 text-sm">
              <Eye size={16} className="text-slate-300" />
              {post.views} views
            </span>
          </div>
        </div>
      </section>

      {/* BODY — light + subtle grid wash */}
      <section className="relative bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100/60 overflow-hidden">
        <CyberGrid opacity={0.05} wash className="z-0" />

        <main className="relative z-10 max-w-5xl mx-auto px-4 py-10">
          {post.featuredImage && (
            <div className="mb-8 rounded-2xl overflow-hidden shadow-lg border border-slate-200 bg-white">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-72 object-cover"
              />
            </div>
          )}

          {/* Content card */}
          <div className="bg-white/90 backdrop-blur-sm border border-slate-200 rounded-2xl shadow-lg p-8 md:p-10">
            <article className="prose max-w-none prose-slate">
              <div
                className="richtext"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </article>

            {post.tags?.length > 0 && (
              <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
                {post.tags.map((t, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-extrabold rounded-full border border-slate-200"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </main>
      </section>
    </div>
  );
};

export default BlogDetail;
